import React from "react";
import { Button, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";

export const ButtonBasic = ({
  children,
  size = "medium",
  widthType = "auto",
  width,
  leftSection,
  rightSection,
  actionType = "primary",
  variant = "contained",
  radius = "small",
  loading = false,
  disabled = false,
  onClick,
  ...props
}) => {
  const theme = useTheme();

  // Convert custom size to MUI size
  const getMuiSize = (size) => {
    switch (size) {
      case "xs":
      case "sm":
        return "small";
      case "md":
        return "medium";
      case "lg":
      case "xl":
        return "large";
      default:
        return "medium";
    }
  };

  // Get additional size styles for xs and xl
  const getAdditionalSizeStyles = (size) => {
    switch (size) {
      case "xs":
        return { 
          fontSize: "0.75rem",
          minHeight: "28px",
          padding: theme.spacing(0.5, 1)
        };
      case "xl":
        return { 
          fontSize: "1.25rem",
          minHeight: "48px",
          padding: theme.spacing(1.5, 3)
        };
      default:
        return {};
    }
  };

  // Width mapping
  const getWidthStyles = (widthType, width) => {
    switch (widthType) {
      case "full":
        return { width: "100%" };
      case "manual":
        return { width: width };
      case "auto":
      default:
        return { width: "auto" };
    }
  };

  // Convert actionType to MUI color
  const getMuiColor = (actionType) => {
    switch (actionType) {
      case "primary":
        return "primary";
      case "secondary":
        return "secondary";
      case "error":
        return "error";
      case "warning":
        return "warning";
      case "info":
        return "info";
      case "success":
        return "success";
      default:
        return "primary";
    }
  };

  // Radius mapping
  const getRadiusStyles = (radius) => {
    switch (radius) {
      case "none":
        return { borderRadius: 0 };
      case "small":
        return { borderRadius: theme.spacing(1) };
      case "full":
        return { borderRadius: "9999px" };
      default:
        return { borderRadius: theme.spacing(1) };
    }
  };

  // Custom color override for primary actionType only
  const getCustomColorStyles = (actionType, variant) => {
    if (actionType === "primary" && variant === "contained") {
      return {
        backgroundColor: theme.customTokens?.colors?.custom?.ui?.dark?.bold || theme.palette.primary.main,
        color: theme.palette.common.white,
        "&:hover": {
          backgroundColor: theme.customTokens?.colors?.gray?.[8] || theme.palette.primary.dark,
        },
      };
    }
    return {};
  };

  const muiSize = getMuiSize(size);
  const muiColor = getMuiColor(actionType);
  const additionalSizeStyles = getAdditionalSizeStyles(size);
  const widthStyles = getWidthStyles(widthType, width);
  const radiusStyles = getRadiusStyles(radius);
  const customColorStyles = getCustomColorStyles(actionType, variant);

  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      variant={variant}
      size={muiSize}
      color={muiColor}
      sx={{
        ...additionalSizeStyles,
        ...widthStyles,
        ...radiusStyles,
        ...customColorStyles,
        textTransform: "none",
        fontWeight: 600,
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
          ...customColorStyles["&:hover"],
        },
        "&:disabled": {
          opacity: loading ? 0.8 : 0.5,
          backgroundColor: loading && actionType === "primary" && variant === "contained" 
            ? theme.customTokens?.colors?.gray?.[6] || theme.palette.action.disabled
            : undefined,
          color: loading && actionType === "primary" && variant === "contained"
            ? theme.palette.common.white
            : undefined,
        },
      }}
      startIcon={
        loading ? (
          <CircularProgress
            size={16}
            sx={{
              color:
                variant === "contained"
                  ? theme.palette.common.white
                  : theme.palette.text.primary,
            }}
          />
        ) : (
          leftSection
        )
      }
      endIcon={rightSection}
      {...props}
    >
      {children}
    </Button>
  );
};

ButtonBasic.propTypes = {
  children: PropTypes.node.isRequired,
  // xs, sm, md, lg, xl
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  // full, auto, manual
  widthType: PropTypes.oneOf(["full", "auto", "manual"]),
  // px, rem, em, %
  width: PropTypes.string,
  // Icon Node
  leftSection: PropTypes.node,
  rightSection: PropTypes.node,
  // primary, secondary, error, warning, info, success
  actionType: PropTypes.oneOf(["primary", "secondary", "error", "warning", "info", "success"]),
  // contained, outlined, text
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  // none, small, full
  radius: PropTypes.oneOf(["none", "small", "full"]),
  // Loading state
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};
